import { map, tap } from "rxjs/operators";
import { Injectable } from "@angular/core";
import {
  CompaniesFilter,
  CompaniesList,
  CompanieSortKeys,
  CompanyProducts,
  CreateCompanyInput,
  SortType,
  UpdateCompanyInput,
} from "../../models/phoenix/globalTypes";
import { Apollo } from "apollo-angular";
import {
  CreateCompanyMutation,
  QueryCompanies,
  QueryCompaniesOverview,
  QueryCompaniesSelect,
  UpdateCompanyMutation,
} from "../queries/phoenix/companies";
import { QueryCompanyEnabledProducts } from "../queries/phoenix/company-enabled-products";
import { Companies } from "src/models/phoenix/Companies";
import { CreateCompany } from "src/models/phoenix/CreateCompany";
import { UpdateCompany } from "src/models/phoenix/UpdateCompany";
import { ToastrService } from "ngx-toastr";
import { CompaniesOverview } from "../../models/phoenix/CompaniesOverview";

export const emptyFilter: CompaniesFilter = {
  ids: [],
  names: [],
  accountIds: [],
  countryCodes: [],
};

export const emptyCompaniesList: CompaniesList = {
  limit: 2000,
  order: {
    key: CompanieSortKeys.name,
    type: SortType.asc,
  },
};

@Injectable({
  providedIn: "root",
})
export class CompaniesService {
  constructor(private apollo: Apollo, private toastr: ToastrService) {}

  companies(
    list: CompaniesList = emptyCompaniesList,
    filter: CompaniesFilter = emptyFilter
  ) {
    return this.apollo
      .use("phoenix")
      .query<CompaniesOverview>({
        query: QueryCompaniesOverview,
        variables: {
          list,
          filter,
          registeredCountriesFilter2: {
            registeredAt: null,
          },
        },
        fetchPolicy: "cache-first",
      })
      .pipe(map((response) => response.data.companies.objects));
  }

  companiesSelect(
    list: CompaniesList = emptyCompaniesList,
    filter: CompaniesFilter = emptyFilter
  ) {
    return this.apollo
      .use("phoenix")
      .query<CompaniesOverview>({
        query: QueryCompaniesSelect,
        variables: {
          list,
          filter,
        },
        fetchPolicy: "cache-first",
      })
      .pipe(map((response) => response.data.companies.objects));
  }

  getNameById(id: string) {
    return this.apollo
      .use("phoenix")
      .query<CompaniesOverview>({
        query: QueryCompaniesOverview,
        variables: {
          list: emptyCompaniesList,
          filter: {
            ids: [id],
            names: [],
            accountIds: [],
            countryCodes: [],
          },
        },
        fetchPolicy: "cache-first",
      })
      .pipe(map((response) => response.data.companies.objects[0].name));
  }

  byId(companyId: string) {
    const list = {
      skip: 0,
      limit: 1,
    };

    const filter = {
      ...emptyFilter,
      ids: [companyId],
    };

    return this.apollo
      .use("phoenix")
      .query<Companies>({
        query: QueryCompanies,
        variables: {
          list,
          filter,
          registeredCountriesFilter2: {
            registeredAt: null,
          },
        },
      })
      .pipe(map((response) => response.data.companies.objects));
  }

  create(input: CreateCompanyInput) {
    return this.apollo
      .use("phoenix")
      .mutate<CreateCompany>({
        mutation: CreateCompanyMutation,
        variables: {
          input,
          filter: {
            registeredAt: null,
          },
        },
        // refetchQueries: [
        //   {
        //     query: QueryAccounts,
        //     variables: { list: emptyAccountsList, filter: emptyAccountsFilter },
        //   },
        // ],
      })
      .pipe(
        tap((response) => this.toastr.success("Created Successfuly!")),
        map((response) => response.data.createCompany)
      );
  }

  update(input: UpdateCompanyInput) {
    return this.apollo
      .use("phoenix")
      .mutate<UpdateCompany>({
        mutation: UpdateCompanyMutation,
        variables: {
          input,
          filter: {
            registeredAt: null,
          },
        },
        // refetchQueries: [
        //   {
        //     query: QueryAccounts,
        //     variables: { list: emptyAccountsList, filter: emptyAccountsFilter },
        //   },
        // ],
      })
      .pipe(
        tap((response) => this.toastr.success("Updated Successfuly!")),
        map((response) => response.data.updateCompany)
      );
  }

  /**
   * Fetches companies with enabled products.
   *
   * @param enabledProduct - The product to filter by (e.g., "apai")
   * @param companyIds - Array of company IDs to filter by. Empty array for admins to get all companies.
   * @param list - Optional pagination and sorting parameters
   * @returns Observable of companies with enabled products
   */
  companiesWithEnabledProducts(
    enabledProduct: CompanyProducts,
    companyIds: string[],
    list: CompaniesList = emptyCompaniesList
  ) {
    const filter: CompaniesFilter = {
      enabledProducts: [enabledProduct],
      ids: companyIds,
      names: [],
      accountIds: [],
      countryCodes: [],
    };

    return this.apollo
      .use("phoenix")
      .query<any>({
        query: QueryCompanyEnabledProducts,
        variables: {
          list,
          filter,
        },
        fetchPolicy: "network-only",
      })
      .pipe(
        map((response) =>
          response.data.companies.objects.map((company) => ({
            id: company.id,
            name: company.name,
            enabled: true,
          }))
        )
      );
  }
}
